(() => {
  const bindOutsideClose = () => {
    const drawer = document.getElementById('Drawer-Search');
    if (!drawer || drawer.dataset.outsideCloseBound === 'true') return;

    const overlay = drawer.querySelector(':scope > .f-drawer__overlay');
    const closer = drawer.querySelector('drawer-closer.f-drawer__close');
    if (!overlay || !closer) return;

    drawer.dataset.outsideCloseBound = 'true';
    overlay.addEventListener('click', (event) => {
      if (event.target !== overlay) return;
      closer.click();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOutsideClose, { once: true });
  } else {
    bindOutsideClose();
  }
})();

(() => {
  const INPUT_SELECTOR = '#Drawer-Search [data-predictive-search-input]';
  const MOBILE_QUERY = '(max-width: 767.98px)';
  const LIVE_DELAY = 140;
  const timers = new WeakMap();
  const composingInputs = new WeakSet();

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;
  const getDrawer = () => document.getElementById('Drawer-Search');

  const updateMobileViewportHeight = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    if (!isMobile()) {
      drawer.style.removeProperty('--peakstyle-mobile-viewport-height');
      return;
    }

    const height = Math.round(window.visualViewport?.height || window.innerHeight || 0);
    if (height > 0) {
      drawer.style.setProperty('--peakstyle-mobile-viewport-height', `${height}px`);
    }
  };

  const injectLiveStyles = () => {
    if (document.getElementById('peakstyle-live-search-styles')) return;

    const style = document.createElement('style');
    style.id = 'peakstyle-live-search-styles';
    style.textContent = `
      #Drawer-Search predictive-search[loading][results="true"] [data-predictive-search] {
        position: relative;
      }
      #Drawer-Search predictive-search[loading][results="true"] [data-predictive-search]::before {
        content: "Ergebnisse werden aktualisiert …";
        display: flex;
        align-items: center;
        justify-content: center;
        position: sticky;
        inset-block-start: 0;
        z-index: 4;
        min-height: 4.4rem;
        margin-block-end: 1.2rem;
        padding: .8rem 1.2rem;
        background: rgba(var(--color-background), .94);
        border-bottom: 1px solid var(--color-border);
        font-size: 1.3rem;
        font-weight: 600;
        backdrop-filter: blur(4px);
      }
      #Drawer-Search predictive-search[loading][results="true"] [data-predictive-search] .f-predictive-search__results-list:first-child {
        display: grid !important;
        opacity: .58;
        pointer-events: none;
        transition: opacity .16s ease;
      }
      #Drawer-Search predictive-search:not([loading]) [data-predictive-search] .f-predictive-search__results-list {
        opacity: 1;
        transition: opacity .16s ease;
      }
      #Drawer-Search .peakstyle-search-full-results-wrap {
        padding: 0 2rem 2.4rem;
      }
      #Drawer-Search .peakstyle-search-full-results {
        display: flex !important;
        align-items: center;
        justify-content: center !important;
        width: 100%;
        min-height: 4.8rem;
        padding: 1.2rem 1.6rem !important;
        border: 1px solid currentColor;
        text-align: center;
      }
      @media (max-width: 767.98px) {
        #Drawer-Search {
          height: var(--peakstyle-mobile-viewport-height, 100dvh) !important;
          max-height: var(--peakstyle-mobile-viewport-height, 100dvh) !important;
        }
        #Drawer-Search .f-drawer__content,
        #Drawer-Search .f-search-drawer {
          height: var(--peakstyle-mobile-viewport-height, 100dvh) !important;
          max-height: var(--peakstyle-mobile-viewport-height, 100dvh) !important;
          min-height: 0 !important;
        }
        #Drawer-Search predictive-search,
        #Drawer-Search .f-search-drawer__form {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
        #Drawer-Search .f-drawer__header {
          position: relative;
          z-index: 8;
          flex: 0 0 auto;
          background: var(--color-header-bg);
        }
        #Drawer-Search .f-drawer__scroller {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          padding-bottom: calc(1.6rem + env(safe-area-inset-bottom));
          scroll-padding-top: 1rem;
          scroll-padding-bottom: calc(7.2rem + env(safe-area-inset-bottom));
        }
        #Drawer-Search .peakstyle-search-full-results-wrap {
          position: sticky;
          inset-block-end: 0;
          z-index: 7;
          margin: 0 -2rem;
          padding: 1.2rem 2rem calc(1.2rem + env(safe-area-inset-bottom));
          background: var(--color-header-bg);
          border-top: 1px solid var(--color-border);
        }
      }
    `;
    document.head.appendChild(style);
  };

  const updateBusyState = (search) => {
    if (search.hasAttribute('loading')) {
      search.setAttribute('aria-busy', 'true');
    } else {
      search.removeAttribute('aria-busy');
    }
  };

  const focusResults = (search) => {
    const target = search.predictiveSearchResults || search.querySelector('[data-predictive-search]');
    if (!target) return;

    if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
    try {
      target.focus({ preventScroll: true });
    } catch (_) {
      target.focus();
    }
  };

  const scrollResultsIntoView = (search, behavior = 'smooth') => {
    const drawer = getDrawer();
    const scroller = drawer?.querySelector('.f-drawer__scroller');
    const target = search.querySelector(
      '.f-predictive-search__result-products, .f-predictive-search__empty, [data-predictive-search]'
    );
    if (!scroller || !target) return;

    window.requestAnimationFrame(() => {
      const scrollerRect = scroller.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = scroller.scrollTop + targetRect.top - scrollerRect.top - 8;
      scroller.scrollTo({ top: Math.max(0, top), behavior });
    });
  };

  const revealMobileResults = (search) => {
    if (!isMobile()) return;

    updateMobileViewportHeight();
    search.open?.();
    focusResults(search);
    window.setTimeout(() => scrollResultsIntoView(search, 'smooth'), 80);
    window.setTimeout(() => {
      updateMobileViewportHeight();
      scrollResultsIntoView(search, 'auto');
    }, 320);
  };

  const scheduleSearch = (input) => {
    const search = input.closest('predictive-search');
    if (!search || typeof search.onChange !== 'function') return;

    const previousTimer = timers.get(input);
    if (previousTimer) window.clearTimeout(previousTimer);

    const query = input.value.trim();
    const delay = query.length ? LIVE_DELAY : 0;
    const timer = window.setTimeout(() => {
      timers.delete(input);
      search.onChange();
    }, delay);

    timers.set(input, timer);
  };

  const handleMobileEnter = (event, input, search) => {
    if (event.key !== 'Enter' || !isMobile()) return;

    event.preventDefault();
    event.stopPropagation();

    const query = input.value.trim();
    if (!query) return;

    const pendingTimer = timers.get(input);
    if (pendingTimer) window.clearTimeout(pendingTimer);
    timers.delete(input);

    search.dataset.mobileSubmitPending = 'true';
    search.open?.();

    const currentQuery = String(search.searchTerm || '').trim();
    const needsSearch = currentQuery !== query
      || (!search.hasAttribute('loading') && search.getAttribute('results') !== 'true');

    if (needsSearch && typeof search.onChange === 'function') {
      search.onChange();
    }

    focusResults(search);

    if (!search.hasAttribute('loading') && search.getAttribute('results') === 'true') {
      delete search.dataset.mobileSubmitPending;
      revealMobileResults(search);
    }
  };

  const bindInput = (input) => {
    if (input.dataset.peakstyleLiveResultsBound === 'true') return;

    const search = input.closest('predictive-search');
    if (!search) return;

    input.dataset.peakstyleLiveResultsBound = 'true';

    input.addEventListener('compositionstart', () => {
      composingInputs.add(input);
    }, true);

    input.addEventListener('compositionend', () => {
      composingInputs.delete(input);
      scheduleSearch(input);
    }, true);

    input.addEventListener('input', (event) => {
      event.stopImmediatePropagation();
      if (event.isComposing || composingInputs.has(input)) return;
      scheduleSearch(input);
    }, true);

    input.addEventListener('keydown', (event) => {
      handleMobileEnter(event, input, search);
    }, true);

    const busyObserver = new MutationObserver(() => {
      updateBusyState(search);

      if (
        search.dataset.mobileSubmitPending === 'true'
        && !search.hasAttribute('loading')
        && search.getAttribute('results') === 'true'
      ) {
        delete search.dataset.mobileSubmitPending;
        revealMobileResults(search);
      }
    });
    busyObserver.observe(search, {
      attributes: true,
      attributeFilter: ['loading', 'results', 'open']
    });
    updateBusyState(search);
  };

  const bindLiveResults = async () => {
    await customElements.whenDefined('predictive-search');
    injectLiveStyles();
    updateMobileViewportHeight();
    document.querySelectorAll(INPUT_SELECTOR).forEach(bindInput);

    const drawer = getDrawer();
    if (!drawer || drawer.dataset.liveResultsObserverBound === 'true') return;

    drawer.dataset.liveResultsObserverBound = 'true';
    const observer = new MutationObserver(() => {
      drawer.querySelectorAll('[data-predictive-search-input]').forEach(bindInput);
    });
    observer.observe(drawer, { childList: true, subtree: true });
  };

  window.visualViewport?.addEventListener('resize', updateMobileViewportHeight);
  window.visualViewport?.addEventListener('scroll', updateMobileViewportHeight);
  window.addEventListener('resize', updateMobileViewportHeight);
  window.addEventListener('orientationchange', () => {
    window.setTimeout(updateMobileViewportHeight, 120);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindLiveResults, { once: true });
  } else {
    bindLiveResults();
  }
})();

(() => {
  const SEARCH_INPUT = '[data-predictive-search-input]';

  document.addEventListener('keyup', (event) => {
    if (event.code !== 'ArrowUp' || !event.target?.matches?.(SEARCH_INPUT)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    event.target.closest('predictive-search')?.switchOption?.('up');
  }, true);

  customElements.whenDefined('predictive-search').then(() => {
    const SearchClass = customElements.get('predictive-search');
    const proto = SearchClass?.prototype;
    if (!proto || proto.__peakstyleFormlessRuntimeFix) return;

    proto.__peakstyleFormlessRuntimeFix = true;
    const applyVariantCriteria = proto.applyVariantCriteria;
    if (typeof applyVariantCriteria !== 'function') return;

    proto.applyVariantCriteria = async function(parsed) {
      const result = await applyVariantCriteria.call(this, parsed);
      const liveCount = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (liveCount?.textContent?.includes('Änhnliche')) {
        liveCount.textContent = liveCount.textContent.replaceAll('Änhnliche', 'Ähnliche');
      }
      return result;
    };
  });
})();
